import type { ContentTestWidget } from '@inference.engine/widgets/web';
import { useTranslation } from 'react-i18next';

import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';

const MAX_PREVIEW_INSIGHTS = 2;

interface Props {
  data: ContentTestWidget;
}

/**
 * Full-bleed hero resolved live from the tested image asset. Keeps a neutral
 * placeholder while the download-url query is in flight so the card never
 * flashes a broken image. (Read-only cached fetch — never the widget store.)
 */
function ContentTestHero(props: { assetId: string; alt: string }) {
  const { downloadUrl } = useGetDownloadUrl(props.assetId);
  return (
    <div className="h-full w-full bg-[#EFEFF0]">
      {downloadUrl && (
        <img src={downloadUrl} alt={props.alt} className="h-full w-full object-cover" />
      )}
    </div>
  );
}

const STATUS_STYLES: Record<string, string> = {
  completed: 'bg-emerald-100 text-emerald-700',
  running: 'bg-accent text-primary-hover',
  draft: 'bg-slate-100 text-slate-600',
  failed: 'bg-red-100 text-red-700',
};

function StatusChip({ status }: { status: string | undefined }) {
  if (!status) return null;
  const style = STATUS_STYLES[status] ?? 'bg-slate-100 text-slate-600';
  return (
    <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${style}`}>
      {status}
    </span>
  );
}

/**
 * Store-free, prop-only Library thumbnail for a content-test artifact. The saved
 * artifact carries a self-contained snapshot in its `data`, so — like the other
 * saved kinds — the thumbnail renders straight from `props.data` and keeps
 * working even if the source content test is later removed.
 *
 * For an IMAGE content test it renders the tested image as a full-bleed hero
 * (the content being tested is the most thumbnail-worthy thing). Text and video
 * tests — and image tests whose asset hasn't resolved — fall back to the
 * name / status / score / insights summary.
 */
export const ContentTestThumbnail = (props: Props) => {
  const { t } = useTranslation('contentTest');
  const test = props.data;
  const name = test.name || t('contentTest.contentTestThumbnail.contentTest');
  const status = test.status;
  const overallScore = test.result?.overallScore;
  const insights = (test.result?.insights ?? []).slice(0, MAX_PREVIEW_INSIGHTS);

  // Hero only for image content with a resolvable asset; video assets are not
  // still images (useGetDownloadUrl fetches them as images) so they use text.
  if (test.contentType === 'image' && test.mediaAssetId) {
    return <ContentTestHero assetId={test.mediaAssetId} alt={name} />;
  }

  return (
    <div className="flex h-full flex-col gap-2 overflow-hidden">
      <div className="flex items-center justify-between gap-2">
        <p className="line-clamp-2 text-sm font-semibold text-[#1A1A1A]">{name}</p>
        <StatusChip status={status} />
      </div>
      {overallScore !== undefined && (
        <p className="text-xs text-[#6B6B6F]">
          {/* eslint-disable-next-line i18next/no-literal-string -- score denominator: digits and a solidus, no word to translate */}
          <span className="text-base font-semibold text-[#1A1A1A]">{overallScore}</span> / 100
        </p>
      )}
      {insights.length > 0 && (
        <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-hidden">
          {insights.map((insight, index) => (
            <li key={index} className="line-clamp-2 text-[10px] leading-snug text-[#6B6B6F]">
              {insight}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
